import React, { useEffect, useState } from 'react';
import { useUIStore } from '../../stores/uiStore';

const SEVERITY_STYLES: Record<string, string> = {
  success: 'bg-green-600 text-white',
  error: 'bg-red-600 text-white',
  warning: 'bg-amber-500 text-white',
  info: 'bg-blue-600 text-white',
};

export const ToastContainer: React.FC = () => {
  const { toasts, removeToast } = useUIStore();

  return (
    <div data-testid="toast-container" aria-live="polite" role="status">
      {toasts.map((toast, index) => (
        <ToastItem
          key={toast.id}
          toast={toast}
          index={index}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </div>
  );
};

interface ToastItemProps {
  toast: {
    id: string;
    type: 'success' | 'error' | 'warning' | 'info';
    message: string;
    duration?: number;
    action?: {
      label: string;
      onClick: () => void;
    };
  };
  index: number;
  onClose: () => void;
}

const ToastItem: React.FC<ToastItemProps> = ({ toast, index, onClose }) => {
  const [entered, setEntered] = useState(false);
  const duration = toast.duration ?? (toast.type === 'error' ? 6000 : 4000);

  useEffect(() => {
    const enterTimer = setTimeout(() => setEntered(true), 10);
    const hideTimer = setTimeout(() => {
      onClose();
    }, duration);

    return () => {
      clearTimeout(enterTimer);
      clearTimeout(hideTimer);
    };
  }, [duration, onClose]);

  const getTitle = () => {
    switch (toast.type) {
      case 'success': return '成功';
      case 'error': return 'エラー';
      case 'warning': return '警告';
      case 'info': return 'お知らせ';
      default: return '';
    }
  };

  const bottomOffset = 124 + index * 80;

  return (
    <div
      className={[
        'fixed right-4 z-[9999]',
        'transition-transform duration-300',
        entered ? 'translate-x-0' : 'translate-x-full',
      ].join(' ')}
      style={{ bottom: bottomOffset }}
      {...(toast.type === 'error' ? { 'aria-live': 'assertive' as const, role: 'alert' } : {})}
    >
      <div
        data-testid={toast.type === 'success' ? 'toast-success' : toast.type === 'error' ? 'toast-error' : `toast-${toast.type}`}
        className={[
          'min-w-[300px] max-w-[450px] rounded-lg shadow-lg p-4',
          SEVERITY_STYLES[toast.type],
        ].join(' ')}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="font-semibold mb-1">{getTitle()}</p>
            <span className="text-sm">{toast.message}</span>
            {toast.action && (
              <button
                data-testid="toast-action-button"
                onClick={toast.action.onClick}
                style={{
                  display: 'block',
                  background: 'none',
                  border: 'none',
                  color: 'inherit',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  padding: 0,
                  marginTop: '4px',
                  fontSize: '0.875rem',
                }}
              >
                {toast.action.label}
              </button>
            )}
          </div>
          <button
            onClick={onClose}
            className="flex-shrink-0 opacity-70 hover:opacity-100 transition-opacity leading-none text-lg"
            aria-label="閉じる"
          >
            ×
          </button>
        </div>
      </div>
    </div>
  );
};
